import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ListChecks, Plus } from "lucide-react";
import Modal from "../Modal";
import Btn from "../Btn";
import { useAuth } from "../../context/AuthContext";
import { todoTaskApi } from "../../api/todoTaskApi";
import TodoTaskDetailModal from "./TodoTaskDetailModal";
import TodoTaskFormModal from "./TodoTaskFormModal";
import {
  ACTION_ROLE, Chip, DueTag, NewDot, PriorityTag, PromisedTag, TODO_CHANGED, TodoFlashStyle, unwrap,
} from "./todoShared";

const POLL_MS = 60_000;
/** Once per browser session per login — on sign-in, or whenever the ERP is opened fresh. */
export const popupSeenKey = (userId) => `todo_popup_seen_${userId || ""}`;

/** Live summary: refreshes on every change, on focus and every minute. */
export function useTodoSummary(enabled = true) {
  const [summary, setSummary] = useState(null);
  const refresh = useCallback(async () => {
    try {
      setSummary(unwrap(await todoTaskApi.summary()));
    } catch {
      // Best effort — keep the last summary on a failed poll.
    }
  }, []);
  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    window.addEventListener(TODO_CHANGED, refresh);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener(TODO_CHANGED, refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [enabled, refresh]);
  return { summary, refresh };
}

export const openTodoPage = () => { window.location.hash = "todo"; };

/** The pressing tasks, one row each — dashboard card and login popup. */
export function TodoFocusList({ items, onOpen }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {items.map((t) => {
        const role = ACTION_ROLE[t.actionRole] || ACTION_ROLE.TO_DO;
        const who = t.outside ? `outside · ${t.outsideParty || ""}`
          : t.actionRole === "TO_CHECK" ? `done by ${t.assignedToName}`
          : t.actionRole === "DECLINED" ? `${t.assignedToName} declined${t.declineReason ? `: ${t.declineReason}` : ""}`
          : t.actionRole === "TO_ACCEPT" ? `from ${t.assignedByName} — accept or decline`
          : t.actionRole === "WAITING_ON_YOU" ? `${t.assignedToName} is waiting on you`
            : t.actionRole === "YOUR_WAITING" ? `waiting on ${t.blockedByName || "someone"}`
              : t.assignedById === t.assignedToId ? "your own task" : `from ${t.assignedByName}`;
        return (
          <button key={t.id} type="button" onClick={() => onOpen(t.id)} style={{
            display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", cursor: "pointer",
            padding: "9px 11px", borderRadius: 9, fontFamily: "inherit",
            border: `1px solid ${t.overdue ? "var(--danger-bdr, var(--danger))" : "var(--border)"}`,
            background: t.overdue ? "var(--danger-bg)" : "var(--bg-card)",
          }}>
            {t.unread ? <NewDot /> : <span style={{ width: 8, flexShrink: 0 }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-0)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {t.title}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--text-2)", marginTop: 1 }}>
                <span style={{ color: role.fg, fontWeight: 700 }}>{role.label}</span> · {who}
                {t.status === "REOPENED" && <span style={{ color: "var(--danger)", fontWeight: 600 }}> · sent back</span>}
              </div>
            </div>
            <div style={{ display: "flex", gap: 4, flexShrink: 0, alignItems: "center" }}>
              <PriorityTag priority={t.priority} />
              <DueTag task={t} />
              {t.actionRole !== "TO_CHECK" && t.actionRole !== "DECLINED" && <PromisedTag task={t} />}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function Counts({ s }) {
  if (!s) return null;
  const items = [
    s.forYou > 0 && <Chip key="f" bg="var(--info-bg)" fg="var(--info)" strong>{s.forYou} for you</Chip>,
    s.toAccept > 0 && <Chip key="a" bg="var(--info-bg)" fg="var(--info)" strong>{s.toAccept} to accept</Chip>,
    s.waitingOnYou > 0 && <Chip key="w" bg="var(--warn-bg)" fg="var(--warn)" strong>{s.waitingOnYou} waiting on you</Chip>,
    s.toCheck > 0 && <Chip key="c" bg="var(--success-bg)" fg="var(--success)" strong>{s.toCheck} to check</Chip>,
    s.declined > 0 && <Chip key="d" bg="var(--danger-bg)" fg="var(--danger)" strong>{s.declined} declined</Chip>,
    s.overdue > 0 && <Chip key="o" bg="var(--danger-bg)" fg="var(--danger)" strong>{s.overdue} overdue</Chip>,
    s.newCount > 0 && <Chip key="n" bg="var(--danger-bg)" fg="var(--danger)" strong>{s.newCount} new</Chip>,
  ].filter(Boolean);
  return <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{items}</div>;
}

/**
 * THE DASHBOARD CARD — pinned first, above everything else on the page.
 * Glows while something is new or overdue.
 */
export function TodoFocusCard() {
  const { summary } = useTodoSummary(true);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  if (!summary) return null;
  const clear = summary.needsAction === 0 && summary.yourWaiting === 0;
  return (
    <div className={summary.flash ? "todo-glow" : undefined} style={{
      background: "var(--bg-card)", borderRadius: 12, padding: 14, marginBottom: 16,
      border: `1px solid ${summary.flash ? "var(--danger)" : "var(--border)"}`,
      borderLeft: `4px solid ${summary.flash ? "var(--danger)" : "var(--primary)"}`,
    }}>
      <TodoFlashStyle />
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: clear ? 0 : 10 }}>
        <ListChecks size={18} style={{ color: summary.flash ? "var(--danger)" : "var(--primary)" }} />
        <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text-0)" }}>Your to-do</div>
        {clear
          ? <span style={{ fontSize: 13, color: "var(--success)", display: "inline-flex", alignItems: "center", gap: 5 }}>
              <CheckCircle2 size={14} /> Nothing waiting on you
            </span>
          : <Counts s={summary} />}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <Btn size="sm" variant="secondary" icon={<Plus size={13} />} onClick={() => setCreating(true)}>New task</Btn>
          <Btn size="sm" onClick={openTodoPage}>Open to-do list</Btn>
        </div>
      </div>
      {!clear && <TodoFocusList items={summary.top} onOpen={setOpenId} />}
      <TodoTaskDetailModal open={Boolean(openId)} taskId={openId} onClose={() => setOpenId(null)} />
      <TodoTaskFormModal open={creating} onClose={() => setCreating(false)} onSaved={() => setCreating(false)} />
    </div>
  );
}

/**
 * THE LOGIN POPUP — once per browser session per login (on sign-in, and
 * whenever the ERP is opened fresh), and only when something needs the
 * user: tasks to accept or do, people waiting on them, work to check,
 * tasks someone declined.
 */
export function TodoLoginPopup() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!user?.id) return undefined;
    const key = popupSeenKey(user.id);
    let seen = false;
    try { seen = sessionStorage.getItem(key) === "1"; } catch { /* storage blocked: show once per page load */ }
    if (seen) return undefined;
    let cancelled = false;
    todoTaskApi.summary()
      .then((res) => {
        if (cancelled) return;
        const s = unwrap(res);
        try { sessionStorage.setItem(key, "1"); } catch { /* ignore */ }
        if (s && s.needsAction > 0) {
          setSummary(s);
          setOpen(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);

  // While it is open, a step taken on a task (from this popup) refreshes the list.
  useEffect(() => {
    if (!open) return undefined;
    const refresh = () => {
      todoTaskApi.summary().then((res) => { const s = unwrap(res); if (s) setSummary(s); }).catch(() => {});
    };
    window.addEventListener(TODO_CHANGED, refresh);
    return () => window.removeEventListener(TODO_CHANGED, refresh);
  }, [open]);

  if (!summary) return null;
  const top = summary.top || [];
  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)} width={620}
        title="Your to-do list"
        subtitle={`${summary.needsAction} task${summary.needsAction === 1 ? "" : "s"} need${summary.needsAction === 1 ? "s" : ""} you`}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Btn variant="secondary" onClick={() => setOpen(false)}>Later</Btn>
            <Btn onClick={() => { setOpen(false); openTodoPage(); }}>Open my to-do list</Btn>
          </div>
        }>
        <TodoFlashStyle />
        {summary.overdue > 0 && (
          <div className="todo-glow" style={{
            display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, marginBottom: 10,
            background: "var(--danger-bg)", color: "var(--danger)", fontSize: 13, fontWeight: 600,
          }}>
            <AlertTriangle size={15} /> {summary.overdue} overdue
          </div>
        )}
        <div style={{ marginBottom: 10 }}><Counts s={summary} /></div>
        <TodoFocusList items={top} onOpen={setOpenId} />
        {top.length === 0 && (
          <div style={{ padding: 16, textAlign: "center", color: "var(--success)", fontSize: 13.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <CheckCircle2 size={15} /> Nothing left waiting on you
          </div>
        )}
        {summary.needsAction > top.length && (
          <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 8 }}>
            and {summary.needsAction - top.length} more on your to-do list
          </div>
        )}
      </Modal>
      <TodoTaskDetailModal open={Boolean(openId)} taskId={openId} onClose={() => setOpenId(null)} />
    </>
  );
}

/** Title for the top-bar icon, e.g. "To-Do — 3 for you — 1 to accept — 1 to check — 2 overdue". */
export function todoIconTitle(s) {
  if (!s) return "To-Do";
  return ["To-Do",
    s.forYou > 0 ? `${s.forYou} for you` : null,
    s.toAccept > 0 ? `${s.toAccept} to accept` : null,
    s.waitingOnYou > 0 ? `${s.waitingOnYou} waiting on you` : null,
    s.toCheck > 0 ? `${s.toCheck} to check` : null,
    s.declined > 0 ? `${s.declined} declined` : null,
    s.overdue > 0 ? `${s.overdue} overdue` : null,
    s.newCount > 0 ? `${s.newCount} new` : null,
  ].filter(Boolean).join(" — ");
}

